import { useAuthenticator } from "@aws-amplify/ui-react";
import { useSuspenseQuery } from "@tanstack/react-query";

import { queries } from "@/api/queries";
import type { AuthAccount } from "@/features/auth";
import { useSignOutWithPreviousRoute } from "@/hooks/usePreviousRoute";
import {
  getIsAuthenticated,
  getUserAttributesEmail,
  getUserUsername,
  handleSignOut,
} from "@/lib/aws-amplify/auth";

interface UseAuthentication {
  account: AuthAccount;
}

export const useAuthenticated = () => {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const isConfiguring = authStatus === "configuring";
  const isAuthenticated = authStatus === "authenticated";

  return { isConfiguring, isAuthenticated };
};

export const useAuthentication = (): UseAuthentication => {
  const { redirectSignInWithPreviousRoute } = useSignOutWithPreviousRoute();
  const { data: account, isError } = useSuspenseQuery<AuthAccount>({
    ...queries.auth.account,
    queryFn: async () => {
      const isAuthenticated = await getIsAuthenticated();
      if (!isAuthenticated) {
        console.warn("unauthenticated in useAuthentication");
        redirectSignInWithPreviousRoute();
      }

      const username = await getUserUsername();
      const email = await getUserAttributesEmail();
      if (!username || !email) {
        console.warn("invalid account in useAuthentication");
        await handleSignOut();
        throw new Error("invalid account");
      }

      const account = {
        username,
        email,
      };
      return account;
    },
    refetchOnWindowFocus: true,
    retry: 1,
  });

  if (isError) {
    redirectSignInWithPreviousRoute();
  }

  return { account };
};
